import { useState, useEffect, useContext, useRef } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Box,
  Button,
  Text,
  useColorMode,
  Icon,
  useDisclosure,
} from "@chakra-ui/react";
import { Page } from "components/page";
import { IoWarningOutline } from "react-icons/io5";
import { AiOutlineDelete } from 'react-icons/ai';
import { AskAuth } from "components/ask-auth";
import { useSession } from "next-auth/react";
import toast from "react-hot-toast";
import { getAccount } from "utils/sessionHelper";
import ProtectedRoute from "components/protected-route";
import { useFirebaseUser } from "../utils/useFirebaseUser";
import { textStyle } from "theme/names";
import { colors } from "theme/colors/colors";
import { hslToHsla } from "utils/helpers";
import { FilePond } from "react-filepond";
import { createThumbnail, deleteMedia, getUserMedias, postMedia } from "controllers/media";
import Loader from "react-spinners/PuffLoader";
import { uploadOnS3 } from "utils/uploadUtils";
import shortid from "shortid";
import { TweetContext } from "context/tweetContext";
import MediaMasonry from "components/media-masonry";
import { ImageEditPopup } from "components/popups/imageEditPopup";

const BUCKET_NAME = process.env.NEXT_PUBLIC_PROJECT_BUCKET;

interface MediaObject {
  fileName: string;
  size: number;
  id: string;
  type: string;
  userId?: string;
  url: string;
  thumbnail?: string;
}

const DeletionModal = ({ handleDelete, isDeleting, isOpen, onClose, itemId }) => {
  useEffect(() => {
    if (!isDeleting && isOpen) {
      onClose();
    }
  }, [isDeleting])

  const cancelRef = useRef(null);
  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Remove this asset from library
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            Please confirm you want to remove this media from your asset library. Please note it will remove the media from the database but will not remove it from LinkedIn if it has ever been used in a post.
          </AlertDialogBody>

          <AlertDialogFooter justifyContent="center">
            <Button
              variant="secondaryDestructive"
              leftIcon={<Icon as={AiOutlineDelete} fontSize="sm" />}
              onClick={async () => {
                if (handleDelete)
                  handleDelete(itemId)
              }}
              isLoading={isDeleting}
              ml={3}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export default function Medias() {
  const isAuthenticated = useFirebaseUser();
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [medias, setMedias] = useState<any[]>([]);
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"
  const [deleting, setDeleting] = useState<boolean>(false);
  const { colorMode } = useColorMode();
  const {
    isOpen: isOpenDelete,
    onOpen: onOpenDelete,
    onClose: onCloseDelete,
  } = useDisclosure();
  const {
    isOpen: isOpenEdit,
    onOpen: onOpenEdit,
    onClose: onCloseEdit,
  } = useDisclosure();
  const tweetContext: any = useContext(TweetContext);

  const fetchData = async () => {
    const response = await getUserMedias(session, { isInfluentLeader: true });
    if (response?.success) {
      setMedias(response?.data);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    if (
      isAuthenticated &&
      session &&
      !hasFetched
    ) {
      fetchData();
    }
  }, [session, isAuthenticated]);


  if (!loading && !session) {
    return <ProtectedRoute />;
  }
  const handleOnUpdateFiles = (filesUpdated) => {
    filesUpdated.forEach(x => {
      if (!x.getMetadata("fileName")) {

        let id = "";
        if (x.file.type.includes("video")) {
          id += "vid-";
        }

        if (x.file.type.includes("gif")) {
          id += "gif-"
        }

        id += shortid.generate();

        let fileName = id;
        let link = "https://ez4cast.s3.eu-west-1.amazonaws.com/userUpload/" + fileName;

        x.setMetadata("id", id);
        x.setMetadata("fileName", fileName);
        x.setMetadata("link", link);
      }
    });

  }
  const handleFilePondServerProcess = async (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
    let fileName = metadata.fileName;
    const allowedType = ['video', 'image', 'gif']
    try {
      if (!allowedType.some((type) => file.type.includes(type))) {
        error('File type not allowed');
        toast.error("File type not allowed");
        return
      }
      const params = {
        Bucket: BUCKET_NAME,
        Key: "userUpload/" + fileName,
        Body: file,
        ACL: 'public-read',
      };

      try {
        const url = await uploadOnS3(params) as string;
        load(url);
        const mediaObj: MediaObject = {
          url: url,
          type: file.type,
          fileName: fileName,
          size: file.size,
          id: metadata.id,
        }
        const { media: newMedia } = await postMedia(session, mediaObj);
        if (file.type.includes("video")) {
          const thumbnailUrl = await createThumbnail(session, { id: metadata.id, isInfluentLeader: true });
          load(thumbnailUrl)
          newMedia.thumbnail = thumbnailUrl;
        }
        setMedias(prevState => {
          return [
            newMedia,
            ...prevState,
          ]
        })
      }
      catch (e) {
        console.log(e);
        throw e;
      }
    }
    catch (e) {
      console.log("Error in upload: " + e, JSON.stringify(e));
      // message = `Upload error: ${e.message}`;
    }
  }

  const handleDelete = async (id) => {
    if (!id)
      return;
    setDeleting(true);
    await deleteMedia(session, { id, isInfluentLeader: true });
    setMedias(prevState => {
      const index = prevState.findIndex((item) => item.id === id);
      if (index === -1) {
        return prevState;
      }
      prevState[index].deleted = true;
      return [...prevState];
    })
    setDeleting(false);
    onCloseDelete();
    toast.success("Asset deleted");
  }

  const openDeleteModal = (id: string) => {
    setDeletingId(id);
    onOpenDelete();
  }

  const openEditModal = (id: string) => {
    setEditingId(id);
    onOpenEdit();
  }
  return (
    <Page
      title="My assets"
      titleIconWrapperProps={{
        flexWrap: "wrap", gap: 4,
      }}
      titleRowWrapperProps={{
        justifyContent: { base: "flex-start", md: "flex-start" }, flexWrap: "wrap",
      }}
    >
      {getAccount(session)?.thWriteAccessToken ? (
        <>
          <Box>
            <Text
              textStyle={textStyle["h2.bold.standard"]}
            >
              Upload a new file to your library
            </Text>
            <Box
              w={"100%"}
              my={6}
              _hover={{
                backgroundColor: colorMode === "dark" ? hslToHsla(colors.primary.darkMode[105], 0.3) : hslToHsla(colors.primary.lightMode.default, 0.2),
                cursor: "pointer",
              }}
            >
              <FilePond
                dropOnPage={false}
                dropOnElement={false}
                dropValidation={false}
                allowPaste={true}
                beforeAddFile={() => true}
                onupdatefiles={handleOnUpdateFiles}
                onprocessfile={(error, file) => {
                  file.abortLoad();
                }}
                allowMultiple={true}
                maxFiles={999}
                name="files"
                labelIdle='
                  <div class="filepond--icon">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 13C21.7348 13 21.4804 13.1054 21.2929 13.2929C21.1054 13.4804 21 13.7348 21 14V18.213C20.9992 18.9519 20.7053 19.6603 20.1828 20.1828C19.6603 20.7053 18.9519 20.9992 18.213 21H5.787C5.04809 20.9992 4.33966 20.7053 3.81717 20.1828C3.29468 19.6603 3.00079 18.9519 3 18.213V14C3 13.7348 2.89464 13.4804 2.70711 13.2929C2.51957 13.1054 2.26522 13 2 13C1.73478 13 1.48043 13.1054 1.29289 13.2929C1.10536 13.4804 1 13.7348 1 14V18.213C1.00132 19.4822 1.50609 20.699 2.40354 21.5965C3.30099 22.4939 4.51782 22.9987 5.787 23H18.213C19.4822 22.9987 20.699 22.4939 21.5965 21.5965C22.4939 20.699 22.9987 19.4822 23 18.213V14C23 13.7348 22.8946 13.4804 22.7071 13.2929C22.5196 13.1054 22.2652 13 22 13Z" fill="#828282"/>
                      <path d="M6.707 8.70703L11 4.41403V17C11 17.2652 11.1054 17.5196 11.2929 17.7071C11.4804 17.8947 11.7348 18 12 18C12.2652 18 12.5196 17.8947 12.7071 17.7071C12.8946 17.5196 13 17.2652 13 17V4.41403L17.293 8.70703C17.4816 8.88919 17.7342 8.98998 17.9964 8.9877C18.2586 8.98543 18.5094 8.88026 18.6948 8.69485C18.8802 8.50944 18.9854 8.25863 18.9877 7.99643C18.99 7.73423 18.8892 7.48163 18.707 7.29303L12.707 1.29303C12.5195 1.10556 12.2652 1.00024 12 1.00024C11.7348 1.00024 11.4805 1.10556 11.293 1.29303L5.293 7.29303C5.11085 7.48163 5.01005 7.73423 5.01233 7.99643C5.01461 8.25863 5.11978 8.50944 5.30518 8.69485C5.49059 8.88026 5.7414 8.98543 6.0036 8.9877C6.2658 8.98998 6.5184 8.88919 6.707 8.70703Z" fill="#828282"/>
                      </svg>
                  </div>
                  <div>Click to <span class="filepond--label-action">Upload</span> or drag & drop</div>
                '
                credits={false}
                className={colorMode === "dark" ? "filepond--panel-dark" : "filepond--panel-light"}
                server={{
                  //@ts-ignore
                  process: handleFilePondServerProcess,
                }}
              />
            </Box>
          </Box>
          <Box w={"100%"} display={"flex"} flexDirection={"column"} >
            <Text
              textStyle={textStyle["h2.bold.standard"]}
            >
              Your assets
            </Text>
            {!hasFetched && (
              <Box
                width={"100%"}
                mt={6}
                display={"flex"}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Loader color={colorMode === "dark" ? hslToHsla(colors.primary.lightMode[100], 1) : hslToHsla(colors.primary.lightMode[400], 1)} />
              </Box>
            )}
            {(hasFetched && (!medias.length || medias.every((item) => !!item.deleted))) && <Box
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              alignItems={"center"}
              p={6}
              w={"100%"}
              mt={6}
              bg={"#ECECEC"}
              color={"#4F4F4F"}
              rounded={5}
              _dark={{
                background: "#1E1E1E",
                color: "#F2F2F2",
              }}
            >
              <IoWarningOutline size={30} />
              <Text p={2} display={"flex"}>You haven't saved any asset (image, video) yet. Add some above.</Text>
            </Box>}
            <Box mt={6}>
              {(hasFetched && !!medias.length) && <MediaMasonry colorMode={colorMode} handleDelete={handleDelete} openDeleteModal={openDeleteModal} openEditModal={openEditModal} hasEdit medias={medias} tweetContext={tweetContext} />}
            </Box>
          </Box>
          <DeletionModal
            itemId={deletingId}
            onClose={onCloseDelete}
            isOpen={isOpenDelete}
            handleDelete={handleDelete}
            isDeleting={deleting}
          />
          {editingId && <ImageEditPopup
            isOpen={isOpenEdit}
            onClose={onCloseEdit}
            imageId={editingId}
            onSave={(newMedia) => {
              setMedias(prevState => {
                return [
                  newMedia,
                  ...prevState,
                ]
              })
              onCloseEdit();
            }}
          />}
        </>
      ) : (
        <AskAuth />
      )
      }
    </Page >
  );
}